import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  ShieldCheck, Loader2, X, AlertTriangle, Send, Briefcase, Mail, Phone,
  Building2, UserRound, ExternalLink, CheckCircle2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { CANDIDATE_PATH, QUICK_BGV_PARAM } from './quickBgvLink';

// ===========================================================================
// Quick BGV — the landing panel on /ats/bgv
// ===========================================================================
//
// The other half of the Actions -> Quick BGV menu item. It renders at the top
// of the BGV screen when the URL carries ?candidate=<id>, and its job is to
// remove every step between "I am looking at this person's profile" and "their
// background verification is running".
//
// WHAT IT IS REPLACING
// --------------------
// The old Background Verify screen (/ats/verifications) opens one row per
// PREVIOUS EMPLOYER and mails each of them a confirmation request, typed in by
// hand. That is the "we have to mail every single company" complaint. The BGV
// module asks the CANDIDATE for their documents once and runs four checks
// against them, so the only thing Quick BGV has to do is get the right
// candidate into that module without anybody searching for them again.
//
// FOUR RULES THIS FILE MUST NOT BREAK
// -----------------------------------
//
// 1. IT ADDS NO NEW WAY TO OPEN A CASE. Every button here calls an endpoint
//    routes/bgv_intake.py and routes/bgv.py already expose — POST /bgv/cases,
//    POST /bgv/push, POST /bgv/requests/{id}/accept — so the four stages, the
//    document request and the audit trail are identical however a case began.
//    A second creation path is how one of them forgets to email the candidate.
//
// 2. IT NEVER CREATES A SECOND CASE. One case per candidate is the module's
//    central rule. If one already exists this panel opens it instead, and if a
//    creation still comes back 409 (someone else got there first) it finds the
//    existing case and opens that rather than showing an error.
//
// 3. IT NEVER DEAD-ENDS. The candidate id in the URL is enough to act, so even
//    when every identity lookup fails the buttons still work and the panel says
//    plainly what it could not find out. Where something really is missing —
//    an email address, most often — it names the field and links to the page
//    that fixes it.
//
// 4. A RECRUITER IS NOT REFUSED, THEY ARE REDIRECTED. Opening a case is an
//    admin act. For anyone else the same panel sends the request to an admin
//    and says so in a sentence, which is the handoff routes/bgv_intake.py
//    already implements.

// House style, matching BgvPage and the rest of the new DCS screens. Copied
// rather than imported: BgvPage imports this component, so importing its
// constants back would make a cycle for seven hex values.
const C = {
  cream: '#faf7f0', card: '#ffffff', border: '#e5e1d5',
  ink: '#2b2b2b', muted: '#7a7a7a', gold: '#c9a84c', navy: '#2e3a68',
};
const FONT = "'Outfit', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

/** A human sentence out of an axios error, never "Request failed with status
 *  code 409". The backend writes a message for exactly these situations. */
const errText = (e) =>
  e?.response?.data?.detail || e?.message || 'Something went wrong';

/** First non-empty value among several field spellings. Same reason as in
 *  quickBgvLink.js: one fact, several names, depending on which import wrote
 *  the record. */
const pick = (source, fields) => {
  for (const field of fields) {
    const value = source ? source[field] : undefined;
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

/** Turn whatever GET /candidates/{id} returns into a flat record, or null.
 *
 *  The shape of that endpoint is not pinned anywhere in this repo — it lives in
 *  server.py — so this accepts the three envelopes the ATS uses and REJECTS
 *  anything it does not recognise instead of half-filling the card. Guessing an
 *  envelope and silently rendering {} is exactly what made the old BGV
 *  candidate dropdown look empty when the request had actually failed.
 */
function unwrapCandidate(data, expectedId) {
  const body = data && typeof data === 'object'
    ? (data.candidate || data.data || data)
    : null;
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const id = pick(body, ['id', '_id', 'candidate_id']);
  if (id && expectedId && id !== expectedId) return null;   // the wrong person
  // A record with neither an id nor a name is an error body, not a candidate.
  if (!id && !pick(body, ['name', 'full_name', 'candidate_name'])) return null;
  return body;
}

/** The candidate fields this panel shows, from any of the shapes it can get:
 *  the handoff from the candidate page, GET /candidates/{id}, or a card from
 *  the BGV ready queue. */
function toPerson(source, fallbackId) {
  if (!source) return null;
  const employers = Array.isArray(source.employers) ? source.employers : [];
  const company = pick(source, ['company', 'current_company', 'current_employer']);
  return {
    id: pick(source, ['id', '_id', 'candidate_id']) || fallbackId,
    name: pick(source, ['name', 'full_name', 'candidate_name']),
    email: pick(source, ['email', 'email_address', 'personal_email']),
    phone: pick(source, ['phone', 'mobile', 'phone_number', 'contact_number']),
    designation: pick(source, ['designation', 'current_designation',
      'current_role', 'job_title']),
    company,
    location: pick(source, ['location', 'current_location', 'city']),
    code: pick(source, ['code', 'candidate_code', 'resume_number']),
    status: pick(source, ['status', 'stage', 'pipeline_status'])
      || pick(source, ['jm_status', 'jm_pipeline_status']),
    employers: employers.length ? employers : (company ? [company] : []),
  };
}

/** Merge two partial records, preferring whichever already has a value.
 *  The handoff paints the card instantly; the server's answer corrects it. */
function mergePerson(base, extra) {
  if (!base) return extra;
  if (!extra) return base;
  const out = { ...base };
  Object.keys(extra).forEach((key) => {
    const value = extra[key];
    const isEmpty = value === undefined || value === null || value === ''
      || (Array.isArray(value) && value.length === 0);
    if (isEmpty) return;
    const existing = out[key];
    const existingEmpty = existing === undefined || existing === null
      || existing === '' || (Array.isArray(existing) && existing.length === 0);
    if (existingEmpty) out[key] = value;
  });
  return out;
}

// The stages at which BGV is the expected next step. Mirrors
// READY_FOR_BGV_STATUSES in routes/bgv_intake.py — one person's stage is
// spelled several ways in this database, so this normalises before comparing.
// It only decides whether a gentle "this is early" note is shown; nothing here
// blocks anything.
const READY_STAGES = new Set(['selected', 'offered', 'offer_made', 'joined', 'hired']);
const normStage = (value) =>
  String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

const rowStyle = { background: C.card, border: `1px solid ${C.border}` };

function Fact({ icon: Icon, children, muted }) {
  if (!children) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs"
          style={{ color: muted ? C.muted : C.ink }}>
      <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: C.muted }} />
      <span className="truncate">{children}</span>
    </span>
  );
}

export default function QuickBgvPanel({
  cases,          // the case list BgvPage has already loaded
  casesReady,     // false while it is loading OR if loading it failed
  isAdmin,
  onOpenCase,
  onChanged,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const candidateId = (searchParams.get(QUICK_BGV_PARAM) || '').trim();

  // The handoff from the candidate page. Checked against the id in the URL so
  // a stale history entry cannot label this panel with the previous person.
  const handoff = useMemo(() => {
    const state = location.state && location.state.quickBgv;
    return state && state.id === candidateId ? state : null;
  }, [location.state, candidateId]);

  const [person, setPerson] = useState(() => toPerson(handoff, candidateId));
  const [role, setRole] = useState(null);           // the role BGV will attach
  const [pending, setPending] = useState(null);     // an existing bgv_requests row
  const [resolving, setResolving] = useState(true);
  const [identityFailed, setIdentityFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [sent, setSent] = useState(null);           // the accepted push

  // The auto-open must fire once. Without the guard, returning from the case
  // with the Back button re-mounts this panel and it reopens the case, and the
  // user can never get back to the list.
  const openedRef = useRef(false);

  // Does this candidate already have a case? Answered from the list BgvPage has
  // already fetched, so the common case costs no request at all. `casesReady` is
  // false while that fetch is running AND when it FAILED: an empty list caused
  // by a broken endpoint must never be read as "this candidate has no case".
  //
  // Computed during render rather than in the effect so the panel never paints
  // a "Start BGV" button for a fraction of a second before deciding to open the
  // existing case instead.
  const existingCase = useMemo(() => {
    if (!candidateId || !casesReady) return null;
    return (cases || []).find((c) => c.candidate_id === candidateId) || null;
  }, [candidateId, casesReady, cases]);

  /** Take ?candidate= out of the URL, keeping any other parameter.
   *  Called before navigating into a case so Back lands on the case list, and
   *  by the dismiss button. replace: true keeps the browser history clean. */
  const clearParam = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(QUICK_BGV_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // -----------------------------------------------------------------------
  // 1. Already has a case? Open it instead of creating a second one.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!existingCase || openedRef.current) return;
    openedRef.current = true;
    toast.message(
      `${existingCase.candidate_name || 'This candidate'} already has a BGV case — opened it.`,
    );
    clearParam();
    onOpenCase(existingCase.id);
  }, [existingCase, clearParam, onOpenCase]);

  // -----------------------------------------------------------------------
  // 2. Find out who this is, and what BGV already knows about them.
  // -----------------------------------------------------------------------
  // Three independent questions, asked at once and settled independently, so a
  // failure of any one of them still leaves a usable panel:
  //   * who they are            -> GET /candidates/{id}
  //   * has someone sent them   -> GET /bgv/requests?status=pending
  //   * which role will attach  -> GET /bgv/requirement-options?candidate_id=
  // Only if the first fails do we spend a fourth request on the BGV ready
  // queue, which carries the same card for anyone at a selected/offered/joined
  // stage.
  useEffect(() => {
    if (!candidateId) return undefined;
    let alive = true;
    // Start from the handoff every time the id changes, so nothing from the
    // previous candidate can survive into this card.
    setPerson(toPerson(handoff, candidateId));
    setRole(null);
    setPending(null);
    setSent(null);
    setActionError('');
    setResolving(true);
    setIdentityFailed(false);

    (async () => {
      const settled = await Promise.allSettled([
        api.get(`/candidates/${encodeURIComponent(candidateId)}`),
        api.get('/bgv/requests', { params: { status: 'pending', limit: 200 } }),
        api.get('/bgv/requirement-options', { params: { candidate_id: candidateId, limit: 1 } }),
      ]);
      if (!alive) return;
      const [profileRes, requestsRes, rolesRes] = settled;

      let resolved = null;
      if (profileRes.status === 'fulfilled') {
        resolved = toPerson(unwrapCandidate(profileRes.value.data, candidateId), candidateId);
      }

      if (requestsRes.status === 'fulfilled') {
        const rows = requestsRes.value.data?.requests || [];
        setPending(rows.find((r) => r.candidate_id === candidateId) || null);
      }
      // A recruiter only ever sees their OWN pushes on that endpoint, so a null
      // here does not prove nobody has sent this person. That is safe: POST
      // /bgv/push is idempotent per candidate and answers with the first
      // request and duplicate: true, which this panel reports as such.

      if (rolesRes.status === 'fulfilled') {
        const suggested = rolesRes.value.data?.suggested || [];
        setRole(suggested[0] || null);
      }

      if (!resolved) {
        // Fallback: the BGV ready queue holds a full card for everyone the
        // client has said yes to, which is most of the people Quick BGV is
        // used on. include_started=true so somebody with a case is still
        // returned — the auto-open above needs no help, but their name does.
        try {
          const { data } = await api.get('/bgv/ready-queue', {
            params: { include_started: true, limit: 200 },
          });
          if (!alive) return;
          const card = (data.items || []).find((i) => i.id === candidateId);
          if (card) resolved = toPerson(card, candidateId);
        } catch (e) {
          // Deliberately not surfaced on its own: this is the second attempt at
          // a nicety, and the panel works without it. What IS surfaced is the
          // end state — "we could not look this candidate up" — below.
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('Quick BGV: ready-queue fallback failed —', errText(e));
          }
        }
      }

      if (!alive) return;
      // The handoff paints the card first; the server's answer fills the gaps.
      setPerson((current) => mergePerson(current || toPerson(handoff, candidateId), resolved));
      setIdentityFailed(!resolved && !handoff);
      setResolving(false);
    })();

    return () => { alive = false; };
    // `handoff` is derived from the same id and is stable for a given history
    // entry; re-running on it would refetch for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId]);

  // -----------------------------------------------------------------------
  // Recovery: find the case that already exists for this candidate.
  // -----------------------------------------------------------------------
  // Used when a create or a push comes back 409. The server puts the case id in
  // the message, but re-reading the list is exact rather than nearly exact, and
  // it is one request on a path the user is about to leave anyway.
  const recoverExistingCase = useCallback(async () => {
    try {
      const { data } = await api.get('/bgv/cases');
      return (data.cases || []).find((c) => c.candidate_id === candidateId) || null;
    } catch (e) {
      return null;
    }
  }, [candidateId]);

  const goToCase = useCallback((caseId) => {
    openedRef.current = true;
    clearParam();
    if (onChanged) onChanged();
    onOpenCase(caseId);
  }, [clearParam, onChanged, onOpenCase]);

  // -----------------------------------------------------------------------
  // The one button
  // -----------------------------------------------------------------------
  const startCase = async () => {
    setBusy(true);
    setActionError('');
    try {
      // If a recruiter already pushed this person, accept THAT request rather
      // than opening a case beside it — otherwise their push sits in the admin
      // queue for ever and the recruiter never learns it was actioned.
      // Accepting runs create_case_core() either way.
      const { data } = pending
        ? await api.post(`/bgv/requests/${pending.id}/accept`)
        : await api.post('/bgv/cases', { candidate_id: candidateId });

      // requirement_id is deliberately NOT sent. create_case_core() infers it
      // from the four places this database records "worked on this role", and
      // an inferred link is logged as inferred. Passing the same id back would
      // record it as a human's explicit choice, which it was not.
      const caseId = data.case_id || data.case?.id;
      if (!caseId) throw new Error('The case was created but no id came back.');

      toast.success(
        person?.name
          ? `BGV started for ${person.name}. Their document request is on its way.`
          : 'BGV case opened. The candidate has been asked for their documents.',
      );
      if (data.requirement_missing) {
        toast.message('No role is linked yet — link one on the case before completing it.');
      }
      goToCase(caseId);
    } catch (e) {
      if (e?.response?.status === 409) {
        const existing = await recoverExistingCase();
        if (existing) {
          toast.message('This candidate already had a BGV case — opened it.');
          goToCase(existing.id);
          return;
        }
      }
      setActionError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const sendToBgv = async () => {
    setBusy(true);
    setActionError('');
    try {
      const { data } = await api.post('/bgv/push', { candidate_id: candidateId });
      setSent(data);
      setPending(data.request || null);
      if (onChanged) onChanged();
      toast.success(data.duplicate
        ? 'Already with an admin — nothing more to do.'
        : `${person?.name || 'This candidate'} sent to BGV. An admin will start it.`);
    } catch (e) {
      if (e?.response?.status === 409) {
        const existing = await recoverExistingCase();
        if (existing) {
          toast.message('This candidate already has a BGV case — opened it.');
          goToCase(existing.id);
          return;
        }
      }
      setActionError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  if (!candidateId) return null;

  const profileHref = `${CANDIDATE_PATH}/${encodeURIComponent(candidateId)}`;
  const shortId = candidateId.slice(0, 8).toUpperCase();
  const stage = normStage(person?.status);
  const stageIsEarly = Boolean(stage) && !READY_STAGES.has(stage);

  if (existingCase) {
    // The effect above is already navigating into it; this is the frame in
    // between, and it says what is happening rather than flashing a button the
    // user must not press.
    return (
      <div className="mb-6 flex items-center gap-2 rounded-xl px-4 py-3 text-sm"
           style={{ ...rowStyle, color: C.muted, fontFamily: FONT }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Opening the existing BGV case…
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-xl overflow-hidden"
         style={{ ...rowStyle, borderColor: C.gold, fontFamily: FONT }}
         data-testid="quick-bgv-panel">
      <div className="flex items-center justify-between gap-3 px-4 py-2"
           style={{ background: C.cream, borderBottom: `1px solid ${C.border}` }}>
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide"
              style={{ color: C.navy }}>
          <ShieldCheck className="w-4 h-4" style={{ color: C.gold }} /> Quick BGV
        </span>
        <button type="button" onClick={clearParam} aria-label="Dismiss Quick BGV"
                className="rounded p-1 transition-colors hover:bg-white"
                data-testid="quick-bgv-dismiss">
          <X className="w-4 h-4" style={{ color: C.muted }} />
        </button>
      </div>

      <div className="p-4">
        {/* ---- who ---- */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-base font-semibold" style={{ color: C.ink }}>
              <UserRound className="w-4 h-4" style={{ color: C.muted }} />
              {resolving && !person?.name ? (
                <span className="inline-flex items-center gap-2 text-sm" style={{ color: C.muted }}>
                  <Loader2 className="w-4 h-4 animate-spin" /> Looking this candidate up…
                </span>
              ) : (
                person?.name || `Candidate ${shortId}`
              )}
              {person?.status && (
                <Badge className="bg-slate-100 text-slate-700">{person.status}</Badge>
              )}
            </p>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
              <Fact icon={Briefcase}>{person?.designation}</Fact>
              <Fact icon={Building2}>{person?.company}</Fact>
              <Fact icon={Mail} muted={!person?.email}>
                {person?.email || 'No email on file'}
              </Fact>
              <Fact icon={Phone} muted={!person?.phone}>
                {person?.phone || 'No mobile on file'}
              </Fact>
            </div>
            {person?.employers?.length > 1 && (
              <p className="mt-1.5 text-xs" style={{ color: C.muted }}>
                Employment on file: {person.employers.join(' · ')}
              </p>
            )}
            <p className="mt-1.5 text-xs" style={{ color: C.muted }}>
              {role
                ? <>Role: <b style={{ color: C.ink }}>{role.title}</b>
                    {role.location ? ` · ${role.location}` : ''} — attached automatically.</>
                : 'No role linked yet. The case still opens; a role is only needed before the certificate.'}
            </p>
          </div>

          <div className="flex flex-shrink-0 flex-col items-end gap-1.5">
            {isAdmin ? (
              <Button onClick={startCase} disabled={busy} data-testid="quick-bgv-start">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <ShieldCheck className="w-4 h-4 mr-2" />}
                {pending ? 'Accept & start BGV' : 'Start BGV now'}
              </Button>
            ) : sent || pending ? (
              <Badge className="bg-amber-100 text-amber-800">
                <Clock className="w-3 h-3 mr-1" /> With an admin
              </Badge>
            ) : (
              <Button onClick={sendToBgv} disabled={busy} data-testid="quick-bgv-push">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Send className="w-4 h-4 mr-2" />}
                Send to BGV
              </Button>
            )}
            <Link to={profileHref} className="text-[11px] underline hover:no-underline"
                  style={{ color: C.muted }}>
              Back to the profile
            </Link>
          </div>
        </div>

        {/* ---- what happens when the button is pressed ---- */}
        {!sent && (
          <p className="mt-3 text-xs" style={{ color: C.muted }}>
            {isAdmin
              ? (pending
                ? `${pending.requested_by_name || 'A recruiter'} has already sent this candidate to BGV. Accepting opens the case and clears it from the queue.`
                : 'Opens one case with the four checks and emails the candidate a secure link to upload their documents. No employer is contacted at this stage.')
              : 'Sends this candidate to an admin, who opens the case. Opening one emails the candidate a document request and starts a 48-hour clock, so it is an admin action.'}
          </p>
        )}

        {/* ---- the recruiter's confirmation ---- */}
        {sent && (
          <div className="mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-xs"
               style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: C.ink }}
               data-testid="quick-bgv-sent">
            <CheckCircle2 className="mt-0.5 w-4 h-4 flex-shrink-0 text-green-600" />
            <span>
              {sent.duplicate
                ? 'This candidate was already with an admin — nothing more to do.'
                : 'Sent. An admin sees this at the top of their BGV screen and will start it.'}
              {' '}You can close this panel.
            </span>
          </div>
        )}

        {/* ---- the one field that genuinely blocks the process ---- */}
        {person && !person.email && !resolving && (
          <div className="mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-xs"
               style={{ background: '#fffbea', border: `1px solid ${C.gold}`, color: C.ink }}
               data-testid="quick-bgv-missing-email">
            <AlertTriangle className="mt-0.5 w-4 h-4 flex-shrink-0" style={{ color: C.gold }} />
            <span>
              <b>No email address on file.</b> The document request is emailed to the
              candidate, so the case will open but nobody will be asked for anything.
              Add the <b>Email</b> field on{' '}
              <Link to={profileHref} className="underline hover:no-underline">
                {person.name ? `${person.name}'s profile` : 'their profile'}
              </Link>
              {' '}and come back — this button will be waiting.
            </span>
          </div>
        )}

        {/* ---- a note, never a block ---- */}
        {stageIsEarly && !sent && (
          <p className="mt-2 text-xs" style={{ color: C.muted }}>
            Heads up: this candidate is at <b>{person.status}</b>. BGV is normally run
            once the client has said yes — starting it now is allowed, just early.
          </p>
        )}

        {/* ---- we could not look them up at all ---- */}
        {identityFailed && !resolving && (
          <div className="mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-xs"
               style={{ background: '#fffbea', border: `1px solid ${C.gold}`, color: C.ink }}
               data-testid="quick-bgv-unresolved">
            <AlertTriangle className="mt-0.5 w-4 h-4 flex-shrink-0" style={{ color: C.gold }} />
            <span>
              We could not read this candidate&apos;s details here, so the card above is
              blank — but the button still works, because the case is opened from the
              candidate id ({shortId}) and the server looks the rest up itself. If it
              says <i>Candidate not found</i>, the link is stale: open the person from
              the Candidates list and use Actions → Quick BGV again.
            </span>
          </div>
        )}

        {/* ---- an action that failed. Never silent, never a dead end. ---- */}
        {actionError && (
          <div className="mt-3 flex items-start justify-between gap-3 rounded-lg px-3 py-2 text-xs"
               style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' }}
               data-testid="quick-bgv-error">
            <span>{actionError}</span>
            <Link to={profileHref} className="flex flex-shrink-0 items-center gap-1 underline hover:no-underline">
              Open the profile <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
